import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, BoxProps, ButtonV2 } from '@withjoy/joykit';
import { PauseFill, PlayFill, SpeakerOn } from '@withjoy/joykit/icons';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addError } from '@shared/utils/logger';
import { AnalyticsContext } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';

interface AudioPlayerProps {
  id: string;
  url: string;
  preload?: string;
  children?: React.ReactNode;
  width?: BoxProps['width'];
}

export const AudioPlayer = ({ id, url, preload, width = '100%', children }: AudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const hasTrackedAudioListenSummaryRef = useRef(false);
  const analytics = useContext(AnalyticsContext);

  const [isAudioAvailable, setIsAudioAvailable] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const callTelemetry = useEventCallback((state: 'play-start' | 'play-complete' | 'dead-click' | 'error', errorMessage?: string) => {
    analytics.track({ category: 'wedding', action: 'TravelButtonInteracted', label: 'audioPlay', extraInfo: { id, name: 'audioPlay', state, errorMessage } });
  });

  const trackAudioListenSummary = useEventCallback((listenDurationSec: number, hasFinished) => {
    if (hasTrackedAudioListenSummaryRef.current) return;
    hasTrackedAudioListenSummaryRef.current = true;
    analytics.track({
      category: 'wedding',
      action: 'TravelButtonInteracted',
      label: 'audioListenSummary',
      extraInfo: { audioId: id, name: 'audioListenSummary', listenDurationSec, hasFinished },
    });
  });

  const onClickPlayAudio = useEventCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      callTelemetry('dead-click');
      setIsAudioAvailable(false);
      return;
    }
    setIsAudioAvailable(true);
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(error => {
        addError(error, { contextId: id, context: 'Venue intro audio playback failed' });
        callTelemetry('error', error instanceof Error ? error.message : String(error));
      });
    }
  });

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      setIsAudioAvailable(false);
      return;
    }
    setIsAudioAvailable(true);

    const onPlay = () => {
      setIsPlaying(true);
      callTelemetry('play-start');
    };
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      callTelemetry('play-complete');
      trackAudioListenSummary(Math.round(audio.duration), true);
    };
    const onTimeUpdate = () => setProgress((audio.currentTime / audio.duration) * 100);

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('timeupdate', onTimeUpdate);

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('timeupdate', onTimeUpdate);

      if (audio.currentTime > 0) {
        trackAudioListenSummary(Math.round(audio.currentTime), false);
      }
    };
  }, [callTelemetry, trackAudioListenSummary]);

  return (
    <ButtonV2
      variant="outline"
      shape="rounded"
      intent="newPrimary"
      border={'1px solid'}
      borderColor={isPlaying ? 'newUITest1' : 'mono3'}
      color={'mono14'}
      typographyVariant={'body1'}
      _focus={{ border: '1px solid', borderColor: isPlaying ? 'newUITest1' : 'mono3' }}
      _active={{ border: '1px solid', borderColor: isPlaying ? 'newUITest1' : 'mono3' }}
      startIcon={
        <SpeakerOn
          size={24}
          color={'newUITest1'}
        />
      }
      endIcon={
        isPlaying ? (
          <PauseFill
            size={24}
            color={'newUITest1'}
          />
        ) : (
          <PlayFill
            size={24}
            color={'newUITest1'}
          />
        )
      }
      size="md"
      marginTop={5}
      onClick={onClickPlayAudio}
      disabled={!isAudioAvailable}
    >
      {isPlaying ? (
        <Box
          width={width}
          height={pxToRem(4)}
          backgroundColor="mono3"
          borderRadius={pxToRem(5)}
        >
          <Box
            width={`${progress}%`}
            height="100%"
            background="linear-gradient(0deg, #4951EF 0%, #4951EF 100%)"
            borderRadius={pxToRem(5)}
            transition="width 0.2s ease"
          />
        </Box>
      ) : (
        children
      )}
      <Box
        as="audio"
        ref={audioRef}
        src={url}
        preload={preload}
      />
    </ButtonV2>
  );
};
